import { api } from "./client";

// Системни id-та — забранени за редакция/изтриване
export const SYSTEM_PAYMENT_METHOD_IDS: ReadonlySet<number> = new Set([
  1, 2, 3, 4,
]);

export const isSystemPaymentMethod = (id: number) =>
  SYSTEM_PAYMENT_METHOD_IDS.has(id);

/** `system_code` на „Плащане с депозит" (BE `DEPOSIT_SYSTEM_CODE`) — id-то му е
 *  различно във всяка база, затова се познава по кода, не по числото. */
export const DEPOSIT_SYSTEM_CODE = "deposit";

export interface PaymentMethod {
  id: number;
  name_bg: string;
  name_en: string;
  sort_index: number;
  /** „deposit" — системният „Плащане с депозит"; null/липсва за останалите. */
  system_code?: string | null;
}

/** Системен по id (1–4) или по `system_code` — не се трие и не се преименува. */
export const isSystemMethod = (method: Pick<PaymentMethod, "id" | "system_code">) =>
  isSystemPaymentMethod(method.id) || !!method.system_code;

/** Методът „Плащане с депозит" от каталога (`GET /payment-methods`). */
export const depositPaymentMethodOf = (
  methods: readonly PaymentMethod[],
): PaymentMethod | undefined =>
  methods.find((method) => method.system_code === DEPOSIT_SYSTEM_CODE);

export interface PaymentMethodCreatePayload {
  name_bg: string;
  name_en: string;
  sort_index?: number;
}

// Patch-style update — за protected методи (id ∈ {1..4}) пращай само { sort_index }
export type PaymentMethodUpdatePayload = Partial<{
  name_bg: string;
  name_en: string;
  sort_index: number;
}>;

// Извличане на всички начини на плащане — GET /payment-methods
// Бекендът връща записите вече сортирани по sort_index, id — не пресортирай.
export const getPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const response = await api.get<PaymentMethod[]>("/payment-methods");
  return response.data;
};

/**
 * По какъв път би минало плащане с метода за собственика на сесията (BE
 * `payment_methods/print_routes.py`): `pos` — никога през фискалния принтер
 * (`NON_FISCAL_PAYMENT_METHOD_IDS` или „БЕЗ ФИСКАЛИЗИРАНЕ" на устройството),
 * `fiscal` — има истински слот, `none` — нито едното. Ползва се САМО за
 * методите на бакшиша (`payment/tipMath.ts`).
 */
export type PaymentMethodPrintRoute = "fiscal" | "pos" | "none";

export interface ActivePaymentMethod extends PaymentMethod {
  /** Липсва в отговор от по-стар BE и в синтезирания „Смесено". */
  print_route?: PaymentMethodPrintRoute;
}

// Активните начини на плащане за текущата сесия — GET /payment-methods/active
// Връща само методите, които имат стойност в device_payment_methods за
// device-а на сесията (резолва се на бекенда — shared/personal logic).
// Същата структура като /payment-methods + `print_route`.
export const getActivePaymentMethods = async (): Promise<
  ActivePaymentMethod[]
> => {
  const response = await api.get<ActivePaymentMethod[]>(
    "/payment-methods/active",
  );
  return response.data;
};

// Създаване на нов начин на плащане — POST /payment-methods
export const createPaymentMethod = async (
  data: PaymentMethodCreatePayload,
): Promise<PaymentMethod> => {
  const response = await api.post<PaymentMethod>("/payment-methods", data);
  return response.data;
};

// Редакция на начин на плащане — PUT /payment-methods/:id
export const updatePaymentMethod = async (
  id: number,
  data: PaymentMethodUpdatePayload,
): Promise<PaymentMethod> => {
  const response = await api.put<PaymentMethod>(
    `/payment-methods/${id}`,
    data,
  );
  return response.data;
};

// Изтриване на начин на плащане — DELETE /payment-methods/:id
export const deletePaymentMethod = async (id: number): Promise<void> => {
  await api.delete(`/payment-methods/${id}`);
};
